import React from "react";
import { FieldSelectorProps } from "react-querybuilder";
import { DjangoFieldType, FieldData } from "./enums";
import { NoOptionsMessage, resolveFieldPath } from "./utils";
import Select from "react-select";
import { InlineIcon } from '@iconify/react';

import numericIcon from '@iconify-icons/mdi/numeric';
import numeric1Box from '@iconify-icons/mdi/numeric-1-box';
import numericPositive1 from '@iconify-icons/mdi/numeric-positive-1';
import counterIcon from '@iconify-icons/mdi/counter';
import decimalIncrease from '@iconify-icons/mdi/decimal-increase';
import decimalIcon from '@iconify-icons/mdi/decimal';
import calendarIcon from '@iconify-icons/mdi/calendar';
import calendarClock from '@iconify-icons/mdi/calendar-clock';
import clockTimeFourOutline from '@iconify-icons/mdi/clock-time-four-outline';
import checkCircleOutline from '@iconify-icons/mdi/check-circle-outline';
import formatText from '@iconify-icons/mdi/format-text';
import FilterCog from '@iconify-icons/mdi/filter-cog';

import relationOneToMany from '@iconify-icons/tabler/relation-one-to-many';
import relationManyToMany from '@iconify-icons/tabler/relation-many-to-many';
import Json from '@iconify-icons/tabler/json';


const fieldMetaMap: Record<DjangoFieldType, { icon: object; title: string }> = {
    [DjangoFieldType.IntegerField]: {
        icon: numericIcon,
        title: 'Целое число',
    },
    [DjangoFieldType.BigIntegerField]: {
        icon: numericIcon,
        title: 'Целое число',
    },
    [DjangoFieldType.SmallIntegerField]: {
        icon: numeric1Box,
        title: 'Маленькое целое число',
    },
    [DjangoFieldType.PositiveIntegerField]: {
        icon: numericPositive1,
        title: 'Положительное целое число',
    },
    [DjangoFieldType.PositiveSmallIntegerField]: {
        icon: numericPositive1,
        title: 'Положительное маленькое целое число',
    },
    [DjangoFieldType.BigAutoField]: {
        icon: counterIcon,
        title: 'Автоинкрементный счётчик',
    },
    [DjangoFieldType.FloatField]: {
        icon: decimalIncrease,
        title: 'Число с плавающей точкой',
    },
    [DjangoFieldType.DecimalField]: {
        icon: decimalIcon,
        title: 'Десятичное число',
    },

    [DjangoFieldType.DateField]: {
        icon: calendarIcon,
        title: 'Дата',
    },
    [DjangoFieldType.DateTimeField]: {
        icon: calendarClock,
        title: 'Дата и время',
    },
    [DjangoFieldType.TimeField]: {
        icon: clockTimeFourOutline,
        title: 'Время',
    },

    [DjangoFieldType.BooleanField]: {
        icon: checkCircleOutline,
        title: 'Булево значение',
    },
    [DjangoFieldType.JSONField]: {
        icon: Json,
        title: 'JSON',
    },

    [DjangoFieldType.ForeignKey]: {
        icon: relationOneToMany,
        title: 'Связь «один ко многим»',
    },
    [DjangoFieldType.ManyToManyField]: {
        icon: relationManyToMany,
        title: 'Связь «многие ко многим»',
    },

    [DjangoFieldType.CharField]: {
        icon: formatText,
        title: 'Строка',
    },
    [DjangoFieldType.TextField]: {
        icon: formatText,
        title: 'Текст',
    },

    [DjangoFieldType.CUSTOM]: {
        icon: FilterCog,
        title: 'Фильтр',
    },
};

const formatOptionLabel = (option: FieldData) => {
    const fieldType = option.field_type || DjangoFieldType.CUSTOM;
    // @ts-ignore
    const meta = fieldMetaMap[fieldType] || fieldMetaMap[DjangoFieldType.CUSTOM];

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div title={meta.title}>
                <InlineIcon icon={meta.icon} style={{ marginRight: '8px', fontSize: '1.5em' }} />
            </div>
            <span>{option.verbose_name}</span>
        </div>
    );
};


const sharedSelectProps = {
    className: "form-control-select",
    classNamePrefix: "react-select",
    components: { NoOptionsMessage },
    formatOptionLabel,
    styles: {
        control: (base: any) => ({
            ...base,
            minHeight: '38px',
            height: '38px',
        }),
        valueContainer: (base: any) => ({
            ...base,
            height: '38px',
            padding: '0 8px'
        }),
        indicatorsContainer: (base: any) => ({
            ...base,
            height: '38px'
        }),
        menu: (base: any) => ({
            ...base,
            width: 'max-content',
            minWidth: '100%'
        }),
        option: (base: any) => ({
            ...base,
            whiteSpace: 'nowrap'
        })
    }
};

function get_option(fd: FieldData) {
    return {
        value: fd.full_path,
        verbose_name: fd.verbose_name,
        field_type: fd.field_type,
        full_path: fd.full_path,
        label: fd.verbose_name
    }
}

export default function FieldSelector(props: FieldSelectorProps) {
    // @ts-ignore
    let fieldPath = resolveFieldPath(props.options, props.value);

    if (!fieldPath) {
        return (
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Select
                    {...sharedSelectProps}
                    options={props.options.map((rf: any) => get_option(rf))}
                    value={null}
                    onChange={(option: any) => props.handleOnChange(option.value)}
                />
            </div>
        );
    }

    let selects: any = fieldPath.map((field: FieldData, index: number) => {
        if (field.related_fields) {
            let defaultFieldSelected = { ...fieldPath[index], verbose_name: "Выбрать поле", }
            let value = fieldPath[index + 1] || defaultFieldSelected
            // @ts-ignore
            let relatedFields = [defaultFieldSelected, ...field.related_fields];
            const relatedFieldOptions = relatedFields?.map((rf: any) => (get_option(rf)));

            return (
                <Select
                    {...sharedSelectProps}
                    options={relatedFieldOptions}
                    value={value}
                    onChange={(field: FieldData | null) => {
                        if (field) {
                            props.handleOnChange(field.full_path);
                        } else {
                            props.handleOnChange(null); // или другой fallback
                        }
                    }}
                    key={index}
                />)
        }
        return null;
    });

    return (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Select
                {...sharedSelectProps}
                options={props.options.map((rf: any) => (get_option(rf)))}
                value={fieldPath[0]}
                onChange={(option: any) => props.handleOnChange(option.value)}
            />
            {selects}
        </div>
    );
}
